import { Box } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useDeleteView } from '~/api/views';
import { Tooltip } from '~/components/Tooltip';
import { BreadcrumbButtonLink } from '~/components/breadcrumbs/styles';
import { ConfirmDialog } from '~/components/dialogs/confirmation';
import { LayoutSkeleton } from '~/components/layout-skeleton';
import { ListRowHeadingValue } from '~/components/list-row';
import { MetaItem, SettingsBody, SettingsData, SettingsHead } from '~/components/settings';
import { ViewSelect } from '~/components/shows';
import { useAppContext } from '~/contexts';
import { useConfirmDialog } from '~/hooks/dialogs';
import { useLink } from '~/hooks/link';
import { assert } from '~/lib/assert';
import { formatDateOnly } from '~/lib/datetime';
import { EditViewDialog, useEditViewDialog } from './EditViewDialog';
import type { ViewDetails__Show, ViewDetails__View } from './details.generated';

/* GraphQL */ `#graphql
fragment ViewDetails__View on View {
  canDestroy {
    ...AuthorizationResult
  }
  id
  layout {
    id
    name
    ...LayoutSkeleton__Layout
  }
  name
  thumbnailUri
  viewZones {
    id
  }
}

fragment ViewDetails__Show on Show {
  createdAt
  defaultView {
    id
  }
  homeView {
    id
  }
  id
  services {
    id
    name
  }
  views {
    ...ViewDetails__View
  }
}
`;

export interface DetailsProps {
  onViewChange: (viewId: number | undefined) => void;
  show: ViewDetails__Show;
  showFolder?: { id: number; name: string };
  view: ViewDetails__View;
  viewId: number;
}

export const Details = ({ onViewChange, show, showFolder, view, viewId }: DetailsProps) => {
  const { t } = useTranslation();
  const { currentNetwork, currentUser } = useAppContext();
  const canManageViews = currentNetwork.canManageViews.value;
  const [searchParams, setSearchParams] = useSearchParams();
  const link = useLink();
  searchParams.delete('viewId');

  const [deleteView] = useDeleteView();
  const [confirmDelete, confirmDeleteProps] = useConfirmDialog();
  const editDialog = useEditViewDialog();

  assert(typeof view !== 'undefined');

  const handleEdit = useCallback(
    (editViewId: number) => {
      const editView = show.views.find((v) => v.id === editViewId);
      if (editView) {
        editDialog.show(editView);
      }
    },
    [editDialog, show],
  );

  const handleDelete = useCallback(
    async (deleteViewId: number) => {
      if (!(await confirmDelete())) return;
      await deleteView({ variables: { viewId: deleteViewId } });
      if (viewId === deleteViewId) {
        setSearchParams(
          (prev) => {
            prev.set('viewId', String(show.defaultView.id));
            return prev;
          },
          { replace: true },
        );
      }
    },
    [confirmDelete, deleteView, setSearchParams, show.defaultView.id, viewId],
  );

  const orientation = useMemo(() => {
    const { height, width } = view.layout;
    return width > height
      ? t('pages.shows.detail.details.orientation_landscape')
      : t('pages.shows.detail.details.orientation_portrait');
  }, [view, t]);

  const items = useMemo(
    () => [
      ...(show.views.length > 1
        ? [
            {
              heading: t('common.actions.view'),
              value: (
                <Box sx={{ minWidth: canManageViews ? 400 : 250 }}>
                  <ViewSelect
                    fullWidth
                    onChange={onViewChange}
                    onEdit={canManageViews ? handleEdit : undefined}
                    onDelete={canManageViews ? handleDelete : undefined}
                    showActions={canManageViews}
                    showThumbnail
                    show={show}
                    value={viewId}
                  />
                </Box>
              ),
            },
          ]
        : []),
      {
        heading: t('pages.shows.detail.details.layout_label'),
        value: (
          <Tooltip title={view.layout.name}>
            <Box sx={{ width: '100px' }}>
              <LayoutSkeleton
                layout={view.layout}
                zoneSelect={(zoneId) => {
                  const zone = document.getElementById(`zone-${zoneId}`);
                  zone?.offsetParent?.scrollTo({ behavior: 'smooth', top: zone.offsetTop });
                }}
              />
            </Box>
          </Tooltip>
        ),
      },
      ...(view.thumbnailUri
        ? [
            {
              heading: t('common.labels.thumbnail'),
              value: (
                <Box>
                  <img
                    alt={view.name}
                    style={{
                      width: '100px',
                      height: '56px',
                      borderRadius: 4,
                    }}
                    src={view.thumbnailUri}
                  />
                </Box>
              ),
            },
          ]
        : []),
      { heading: t('pages.shows.detail.details.orientation_label'), value: orientation },
      { heading: t('pages.shows.detail.details.zones_label'), value: view.viewZones.length },
      ...(showFolder
        ? [
            {
              heading: t('pages.shows.detail.details.folder_label'),
              value: (
                <BreadcrumbButtonLink
                  to={link({
                    pathname: `/shows/${showFolder.id}`,
                    search: searchParams.toString(),
                  })}
                  variant="text"
                >
                  {showFolder.name}
                </BreadcrumbButtonLink>
              ),
            },
          ]
        : []),
      {
        heading: t('common.labels.created'),
        value: formatDateOnly(show.createdAt),
      },
      ...(currentUser.admin && show.services.length > 0
        ? [
            {
              heading: t('pages.shows.detail.details.services_label'),
              value: show.services.map((x) => x.name).join(', '),
            },
          ]
        : []),
    ],
    [
      show,
      t,
      canManageViews,
      onViewChange,
      handleEdit,
      handleDelete,
      viewId,
      view.layout,
      view.name,
      view.thumbnailUri,
      view.viewZones.length,
      orientation,
      showFolder,
      link,
      searchParams,
      currentUser.admin,
    ],
  );

  return (
    <>
      <SettingsHead>{t('common.labels.details')}</SettingsHead>
      <SettingsBody>
        <SettingsData className="last scroll no-space-between">
          {items.map(({ heading, value }, index) => (
            <MetaItem
              key={heading}
              className={index === 0 ? 'first' : index === items.length - 1 ? 'last' : ''}
            >
              <ListRowHeadingValue heading={heading} value={value} />
            </MetaItem>
          ))}
        </SettingsData>
      </SettingsBody>
      <ConfirmDialog
        {...confirmDeleteProps}
        confirm={t('common.dialogs.delete')}
        deleteConfirm
        prompt={t('pages.shows.detail.details.delete_view_prompt')}
        title={t('pages.shows.detail.details.delete_view_title')}
      />
      <EditViewDialog {...editDialog.props} />
    </>
  );
};
